/**
 * Booking Summary Component
 *
 * Displays booking metrics: total, completed, cancelled, no-shows, rates
 */

import { useTranslation } from "react-i18next";
import type { BookingSummary as BookingSummaryType } from "../types/overview-types";
import { MetricCard } from "./metric-card";
import { MetricCardWrapper } from "./metric-card-wrapper";

interface BookingSummaryProps {
  data: BookingSummaryType;
}

export function BookingSummary({ data }: BookingSummaryProps) {
  const { t } = useTranslation("reports");

  return (
    <div className="space-y-4">
      {/* Booking Counts */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Basic: Total Bookings */}
        <MetricCard
          metric={{ ...data.totalBookings, label: t("metrics.totalBookings.label") }}
          tooltip={t("metrics.totalBookings.tooltip")}
        />

        {/* Basic: Completed Bookings */}
        <MetricCard
          metric={{ ...data.completedBookings, label: t("metrics.completedBookings.label") }}
          tooltip={t("metrics.completedBookings.tooltip")}
        />

        {/* Premium: Cancelled Bookings */}
        <MetricCardWrapper
          metricKey="bookings.cancelledBookings"
          metric={
            data.cancelledBookings
              ? { ...data.cancelledBookings, label: t("metrics.cancelledBookings.label") }
              : undefined
          }
          label={t("metrics.cancelledBookings.label")}
          tooltip={t("metrics.cancelledBookings.tooltip")}
        />

        {/* Premium: No-Show Bookings */}
        <MetricCardWrapper
          metricKey="bookings.noShowBookings"
          metric={
            data.noShowBookings
              ? { ...data.noShowBookings, label: t("metrics.noShowBookings.label") }
              : undefined
          }
          label={t("metrics.noShowBookings.label")}
          tooltip={t("metrics.noShowBookings.tooltip")}
        />

        {/* Premium: Pending Bookings */}
        <MetricCardWrapper
          metricKey="bookings.pendingBookings"
          metric={
            data.pendingBookings
              ? { ...data.pendingBookings, label: t("metrics.pendingBookings.label") }
              : undefined
          }
          label={t("metrics.pendingBookings.label")}
          tooltip={t("metrics.pendingBookings.tooltip")}
        />
      </div>
    </div>
  );
}
